import axiosInstance from "api/axiosInstance";

import type { ProductListItem, ProductDetail, ProductCreateRequest, PolicyOptions, VariantListResponse, VariantStockUpdate, VariantStockUpdateResponse } from "api/types/product.types";

const BASE = "/server/buccl_main/api/v1/products/";

export interface ProductListParams {
  page?: number;
  page_size?: number;
  search?: string;
  is_active?: boolean;
  category?: number;
}

export const productsService = {
  list: (params?: ProductListParams) => axiosInstance.get<ProductListItem[]>(BASE, { params }),

  get: (id: number) => axiosInstance.get<ProductDetail>(`${BASE}${id}/`),

  create: (payload: ProductCreateRequest) => {
    const hasImageData = (payload.images && payload.images.length > 0) || (payload.image_urls && payload.image_urls.length > 0);
    if (!hasImageData) {
      return axiosInstance.post<ProductDetail>(BASE, payload);
    }
    const fd = new FormData();
    const { images, image_urls, image_titles, image_orders, image_thumbnails, options, category_ids, customer_input_options, ...rest } = payload;
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, String(v));
    });
    images?.forEach((file) => fd.append("images", file));
    image_urls?.forEach((url) => fd.append("image_urls", url));
    image_titles?.forEach((t) => fd.append("image_titles", t ?? ""));
    image_orders?.forEach((o) => fd.append("image_orders", String(o)));
    image_thumbnails?.forEach((b) => fd.append("image_thumbnails", String(b)));
    if (category_ids) fd.append("category_ids", JSON.stringify(category_ids));
    if (options) fd.append("options", JSON.stringify(options));
    if (customer_input_options) fd.append("customer_input_options", JSON.stringify(customer_input_options));
    return axiosInstance.post<ProductDetail>(BASE, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  update: (id: number, payload: Partial<ProductCreateRequest>) => {
    const hasImageChanges = (payload.images && payload.images.length > 0) || (payload.image_urls && payload.image_urls.length > 0) || payload.existing_image_ids !== undefined;
    if (!hasImageChanges) {
      return axiosInstance.patch<ProductDetail>(`${BASE}${id}/`, payload);
    }
    const fd = new FormData();
    const { images, image_urls, image_titles, image_orders, image_thumbnails, existing_image_ids, options, category_ids, customer_input_options, ...rest } = payload;
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, String(v));
    });
    images?.forEach((file) => fd.append("images", file));
    image_urls?.forEach((url) => fd.append("image_urls", url));
    image_titles?.forEach((t) => fd.append("image_titles", t ?? ""));
    image_orders?.forEach((o) => fd.append("image_orders", String(o)));
    image_thumbnails?.forEach((b) => fd.append("image_thumbnails", String(b)));
    existing_image_ids?.forEach((imgId) => fd.append("existing_image_ids", String(imgId)));
    if (category_ids) fd.append("category_ids", JSON.stringify(category_ids));
    if (options) fd.append("options", JSON.stringify(options));
    if (customer_input_options) fd.append("customer_input_options", JSON.stringify(customer_input_options));
    return axiosInstance.patch<ProductDetail>(`${BASE}${id}/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  remove: (id: number) => axiosInstance.delete(`${BASE}${id}/`),

  // 옵션 조합(variant) 목록 조회 — 재고 관리용
  getVariants: (id: number) => axiosInstance.get<VariantListResponse>(`${BASE}${id}/variants/`),

  // variant 재고 일괄 수정
  updateVariantStock: (id: number, updates: VariantStockUpdate[]) =>
    axiosInstance.patch<VariantStockUpdateResponse>(`${BASE}${id}/variants/update-stock/`, {
      updates,
    }),

  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return axiosInstance.post<{ url: string }>("/server/buccl_main/api/v1/upload-image/product/", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },

  getPolicyOptions: () => axiosInstance.get<PolicyOptions>("/server/buccl_main/api/v1/admin/policies/options/"),
};
